/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { Fragment, lazy } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { FieldContainer } from '../utils/FieldContainer';
import { PortalConsumer } from '../../../context';
import { FormStateChangeNotifier } from '../form';
var AKFormField = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form/Field');
});
var AKSelect = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.select' */
  '@atlaskit/select');
});
var AKErrorMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.ErrorMessage
    };
  });
});
var AKHelperMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.HelperMessage
    };
  });
});
var validate = function validate(value) {
  return !value ? 'EMPTY' : undefined;
};
var Select = function Select(_ref) {
  var options = _ref.options,
    isMulti = _ref.isMulti,
    defaultValue = _ref.defaultValue,
    label = _ref.label,
    name = _ref.name,
    description = _ref.description,
    isRequired = _ref.isRequired,
    placeholder = _ref.placeholder;
  return jsx(PortalConsumer, null, function (portal) {
    return jsx(FieldContainer, null, jsx(AKFormField, {
      defaultValue: defaultValue,
      label: label,
      name: name,
      isRequired: isRequired,
      validate: isRequired ? validate : undefined
    }, function (_ref2) {
      var fieldProps = _ref2.fieldProps,
        error = _ref2.error;
      var id = fieldProps.id,
        isRequired = fieldProps.isRequired,
        isDisabled = fieldProps.isDisabled,
        isInvalid = fieldProps.isInvalid,
        onChange = fieldProps.onChange,
        onBlur = fieldProps.onBlur,
        onFocus = fieldProps.onFocus,
        value = fieldProps.value,
        ariaLabelledBy = fieldProps['aria-labelledby'];
      return jsx(Fragment, null, jsx(FormStateChangeNotifier, {
        name: name,
        value: fieldProps.value
      }), jsx(AKSelect, {
        id: id,
        isRequired: isRequired,
        isDisabled: isDisabled,
        isInvalid: isInvalid,
        onChange: onChange,
        onBlur: onBlur,
        onFocus: onFocus,
        value: value,
        "aria-labelledby": ariaLabelledBy,
        isMulti: isMulti,
        menuPortalTarget: portal,
        options: options,
        placeholder: placeholder,
        validationState: error ? 'error' : 'default'
      }), error === 'EMPTY' && jsx(AKErrorMessage, null, jsx(FormattedMessage, messages.requiredFieldMessage)), description && jsx(AKHelperMessage, null, description));
    }));
  });
};
var SelectFn = function SelectFn(_ref3) {
  var props = _ref3.props,
    children = _ref3.children;
  var _ref4 = props,
    description = _ref4.description,
    label = _ref4.label,
    name = _ref4.name,
    isMulti = _ref4.isMulti,
    placeholder = _ref4.placeholder,
    isRequired = _ref4.isRequired;
  var defaultValue = [];
  var options = children.map(function (child) {
    var props = child.props,
      type = child.type;
    if (type === 'Option') {
      var _ref5 = props,
        defaultSelected = _ref5.defaultSelected,
        _label = _ref5.label,
        value = _ref5.value;
      var option = {
        label: _label,
        value: value
      };
      if (defaultSelected) {
        if (isMulti) {
          defaultValue.push(option);
        } else {
          defaultValue = [option];
        }
      }
      return option;
    }
  }).filter(function (option) {
    return typeof option !== 'undefined';
  });
  var formattedDefaultValue = isMulti ? defaultValue : defaultValue[0];
  return jsx(Select, {
    label: label,
    name: name,
    options: options,
    isMulti: isMulti,
    isRequired: isRequired,
    defaultValue: formattedDefaultValue,
    description: description,
    placeholder: placeholder
  });
};
var messages = defineMessages({
  requiredFieldMessage: {
    id: 'confluence.select.required.field.message',
    defaultMessage: 'This field is required.',
    description: 'This message alerts the user that a field is required.'
  }
});
export { Select, SelectFn };